import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { PAYMENT_COLORS } from '@/constants/residents/mobile/paymentColors';
import { RNButton } from '@/components';
import { strings } from '@/constants/residents/strings';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { usePaymentMethodActions } from '@/features/payments/hooks/usePaymentMethodActions';
import { 
  hasPaymentMethodCardExpired, 
  formatCardExpiry,
  sortPaymentMethodsByDefault,
} from '@/utils/residents/paymentUtils';
import { PaymentIcons } from '@/components/residents/payments/PaymentIcons';
import { MobileScreenHeader } from '@/components/residents/payments/MobileScreenHeader';
import { mobileScreenStyles } from '@/styles/mobileScreenStyles';

export const MobilePaymentMethodsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { paymentMethods, isLoading, refreshPaymentMethods } = usePaymentData();
  
  // Use shared payment method actions hook
  const { 
    setAsDefault, 
    deleteMethodWithConfirmation, 
    isUpdating, 
    isDeleting 
  } = usePaymentMethodActions(refreshPaymentMethods);

  // Sort methods - default first
  const sortedMethods = useMemo(() => {
    return sortPaymentMethodsByDefault(paymentMethods);
  }, [paymentMethods]);

  const hasNoMethods = sortedMethods.length === 0 && !isLoading;
  const hasOnlyOneMethod = sortedMethods.length === 1;

  const handleAddPaymentMethod = () => {
    navigation.navigate('AddPaymentMethod', { returnTo: 'MobilePaymentMethods' });
  };

  const renderEmptyState = () => (
    <View style={mobileScreenStyles.emptyState}>
      <View style={mobileScreenStyles.emptyIconContainer}>
        <PaymentIcons.CardOutline />
      </View>
      <Text style={mobileScreenStyles.emptyTitle}>No Payment Methods</Text>
      <Text style={mobileScreenStyles.emptyMessage}>
        Add a credit card or bank account to make payments faster
      </Text>
      <RNButton
        title="Add Payment Method"
        onPress={handleAddPaymentMethod}
        variant="tertiary"
        style={styles.addButton}
        textStyle={styles.addButtonText}
      />
    </View>
  );

  const renderPaymentMethod = (method: any, index: number) => {
    const isExpired = hasPaymentMethodCardExpired(method.expiry);
    const isCard = method.type === 'card' || (!method.accttype && method.last4);

    return (
      <View key={method.id || method.sourceId || index} style={styles.methodCard}>
        <View style={styles.methodHeader}>
          <View style={styles.methodIconContainer}>
            {isCard ? <PaymentIcons.Card /> : <PaymentIcons.Bank />}
          </View>
          <View style={styles.methodInfo}>
            <Text style={styles.methodName}>{method.name || 'Payment Method'}</Text>
            <Text style={styles.methodDetails}>
              {isCard 
                ? `Card ending in ${method.last4}` 
                : `Bank account ending in ${method.last4}`
              }
            </Text>
            {isCard && method.expiry && (
              <Text style={[styles.methodExpiry, isExpired && styles.methodExpired]}>
                {isExpired ? 'Expired' : `Expires ${formatCardExpiry(method.expiry)}`}
              </Text>
            )}
          </View>
          {method.isDefault && (
            <View style={styles.defaultBadge}>
              <Text style={styles.defaultBadgeText}>Default</Text>
            </View>
          )}
        </View>

        <View style={styles.methodActions}>
          {!method.isDefault && !isExpired && (
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => setAsDefault(method)}
              disabled={isUpdating}
            >
              <Text style={styles.actionButtonText}>Set as Default</Text>
            </TouchableOpacity>
          )}
          
          {(!method.isDefault || hasOnlyOneMethod) && (
            <TouchableOpacity
              style={[styles.actionButton, styles.deleteButton]}
              onPress={() => deleteMethodWithConfirmation(method)}
              disabled={isDeleting}
            >
              <PaymentIcons.Trash />
              <Text style={styles.deleteButtonText}>Remove</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={mobileScreenStyles.container}>
      <MobileScreenHeader title="Payment Methods" />

      <ScrollView style={mobileScreenStyles.content} contentContainerStyle={mobileScreenStyles.contentContainer}>
        {isLoading ? (
          <View style={mobileScreenStyles.loadingContainer}>
            <ActivityIndicator size="large" color={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY} />
          </View>
        ) : hasNoMethods ? (
          renderEmptyState()
        ) : (
          <>
            <Text style={mobileScreenStyles.sectionDescription}>
              View, add, or remove payment methods
            </Text>
            {sortedMethods.map(renderPaymentMethod)}
            
            <RNButton
              title="+ Add Payment Method"
              onPress={handleAddPaymentMethod}
              variant="outlined"
              style={styles.addAnotherButton}
              textStyle={styles.addAnotherButtonText}
            />
          </>
        )}
      </ScrollView>
    </View>
  );
};

// Icons removed - now using shared PaymentIcons

const styles = StyleSheet.create({
  addButton: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    paddingVertical: 14,
    paddingHorizontal: 32,
  },
  addButtonText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
    fontSize: 16,
  },
  methodCard: {
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  methodHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  methodIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PAYMENT_COLORS.GRAY_LIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  methodDetails: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    marginBottom: 2,
  },
  methodExpiry: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
  },
  methodExpired: {
    color: PAYMENT_COLORS.ERROR_RED,
    fontWeight: '600',
  },
  defaultBadge: {
    backgroundColor: PAYMENT_COLORS.SUCCESS_GREEN_LIGHT,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  defaultBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: PAYMENT_COLORS.SUCCESS_GREEN,
  },
  methodActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: PAYMENT_COLORS.BLUE_LIGHT,
    gap: 6,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
  },
  deleteButton: {
    backgroundColor: '#FEE2E2',
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: PAYMENT_COLORS.ERROR_RED,
  },
  addAnotherButton: {
    borderColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    borderWidth: 2,
    borderStyle: 'dashed',
    marginTop: 8,
    marginBottom: 32,
  },
  addAnotherButtonText: {
    color: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    fontWeight: '600',
  },
});

